import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_CONFIG from './config';
import sessionManager from './utils/sessionManager';
import './ActivationPage.css';
import SiteHeader from './components/SiteHeader';

// Vanilla Lightbox class (copied from ChatPage.js)
class VanillaLightbox {
  constructor() {
    this.eventListeners = [];
  }

  open(photos, startIndex = 0) {
    // Create lightbox overlay
    const overlay = document.createElement('div');
    overlay.className = 'lightbox-overlay';
    overlay.innerHTML = `
      <div class="lightbox-content">
        <button class="lightbox-close">&times;</button>
        <button class="lightbox-prev">&#8249;</button>
        <img class="lightbox-image" src="${photos[startIndex].url}" alt="Property photo" />
        <button class="lightbox-next">&#8250;</button>
        <div class="lightbox-counter">${startIndex + 1} / ${photos.length}</div>
      </div>
    `;

    let currentIndex = startIndex;

    const updateImage = (index) => {
      const img = overlay.querySelector('.lightbox-image');
      const counter = overlay.querySelector('.lightbox-counter');
      img.src = photos[index].url;
      counter.textContent = `${index + 1} / ${photos.length}`;
      currentIndex = index;
    };

    // Event handlers
    const closeHandler = () => this.close(overlay);
    const prevHandler = () => {
      const newIndex = (currentIndex - 1 + photos.length) % photos.length;
      updateImage(newIndex);
    };
    const nextHandler = () => {
      const newIndex = (currentIndex + 1) % photos.length;
      updateImage(newIndex);
    };
    const keyHandler = (e) => {
      if (e.key === 'Escape') closeHandler();
      if (e.key === 'ArrowLeft') prevHandler();
      if (e.key === 'ArrowRight') nextHandler();
    };

    // Add event listeners
    overlay.querySelector('.lightbox-close').addEventListener('click', closeHandler);
    overlay.querySelector('.lightbox-prev').addEventListener('click', prevHandler);
    overlay.querySelector('.lightbox-next').addEventListener('click', nextHandler);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeHandler();
    });
    document.addEventListener('keydown', keyHandler);

    // Store handlers for cleanup
    this.eventListeners.push(
      { type: 'click', handler: closeHandler, element: overlay.querySelector('.lightbox-close') },
      { type: 'click', handler: prevHandler, element: overlay.querySelector('.lightbox-prev') },
      { type: 'click', handler: nextHandler, element: overlay.querySelector('.lightbox-next') },
      { type: 'click', handler: (e) => e.target === overlay && closeHandler(), element: overlay },
      { type: 'keydown', handler: keyHandler, element: document }
    );

    document.body.appendChild(overlay);
  }

  close(overlay) {
    this.removeGlobalEventListeners();
    if (overlay && overlay.parentNode) {
      overlay.parentNode.removeChild(overlay);
    }
  }

  removeGlobalEventListeners() {
    this.eventListeners.forEach(({ type, handler, element }) => {
      element.removeEventListener(type, handler);
    });
    this.eventListeners = [];
  }
}

// Global lightbox instance
const globalLightbox = new VanillaLightbox();

// PhotoGallery component (copied from ChatPage.js)
const PhotoGallery = ({ photos, listingId, showThumbnails = true }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  if (!photos || photos.length === 0) {
    return (
      <div className="no-photos">
        <div className="no-photos-placeholder">
          🏠 No photos available
        </div>
      </div>
    );
  }

  const displayPhotos = photos.slice(0, 8); // Show up to 8 photos

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % displayPhotos.length);
    setImageLoaded(false);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + displayPhotos.length) % displayPhotos.length);
    setImageLoaded(false);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current && touchEndX.current) {
      const swipeDistance = touchStartX.current - touchEndX.current;
      if (swipeDistance > 50) {
        nextPhoto();
      } else if (swipeDistance < -50) {
        prevPhoto();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const currentPhoto = displayPhotos[currentPhotoIndex];

  return (
    <div className="photo-gallery">
      <div 
        className="photo-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={currentPhoto.url}
          alt={`Property ${currentPhotoIndex + 1}`}
          className={`main-photo ${imageLoaded ? 'loaded' : ''}`}
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            console.error('Failed to load image:', currentPhoto.url);
            e.target.style.display = 'none';
          }}
          onClick={() => globalLightbox.open(displayPhotos, currentPhotoIndex)}
        />
        
        {displayPhotos.length > 1 && (
          <>
            <button className="photo-nav prev" onClick={prevPhoto} aria-label="Previous photo">
              &#8249;
            </button>
            <button className="photo-nav next" onClick={nextPhoto} aria-label="Next photo">
              &#8250;
            </button>
            <div className="photo-counter">
              {currentPhotoIndex + 1} / {displayPhotos.length}
              {photos.length > 8 && ` (+${photos.length - 8} more)`}
            </div>
          </>
        )}
      </div>
      
      {showThumbnails && displayPhotos.length > 1 && (
        <div className="photo-thumbnails">
          {displayPhotos.map((photo, index) => (
            <img
              key={index}
              src={photo.url}
              alt={`Thumbnail ${index + 1}`}
              className={`thumbnail ${index === currentPhotoIndex ? 'active' : ''}`}
              onClick={() => {
                setCurrentPhotoIndex(index);
                setImageLoaded(false);
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ActivationPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [activationData, setActivationData] = useState(null);
  const [agentListings, setAgentListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const loadActivationData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://api.rentalhelp.ai/activation/${code}`);
      const data = await response.json();
      
      if (data.success) {
        setActivationData(data.activation);
        
        // Load agent's listings - use agent_id from activation or posted_by from listing
        const agentId = data.activation.agent_id || data.activation.posted_by;
        if (agentId) {
          try {
            const listingsResponse = await fetch(`https://api.rentalhelp.ai/agent-listings/${agentId}?limit=10`);
            const listingsData = await listingsResponse.json();
            if (listingsData.success) {
              // Filter out the current listing from other listings
              const otherListings = listingsData.listings.filter(listing => 
                listing.id !== data.activation.listing_id
              );
              setAgentListings(otherListings);
            }
          } catch (listingsErr) {
            console.error('Error loading agent listings:', listingsErr);
            // Don't fail the whole page if listings fail to load
          }
        }
      } else {
        setError(data.error || 'Invalid or expired activation link');
      }
    } catch (err) {
      console.error('Error loading activation data:', err);
      setError('Failed to load activation details');
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    loadActivationData();
  }, [loadActivationData]);

  const handleSignupClick = () => {
    setShowSignup(true);
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim()) return;

    try {
      setIsSubmitting(true);
      const response = await fetch(`${API_CONFIG.AUTH_BASE_URL}${API_CONFIG.ENDPOINTS.REQUEST_OTP}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phoneNumber: phoneNumber,
          userRole: 'agent'  // Default role for agent signup
        }),
      });

      const data = await response.json();
      if (data.success) {
        setShowVerification(true);
      } else {
        alert(data.message || 'Failed to send verification code');
      }
    } catch (err) {
      console.error('Error sending verification:', err);
      alert('Failed to send verification code');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) return;

    try {
      setIsSubmitting(true);
      const response = await fetch('https://api.rentalhelp.ai/auth/verify-and-complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: phoneNumber,
          verification_code: verificationCode,
          role: 'agent',
          activation_code: code
        }),
      });

      const data = await response.json();
      console.log('🔍 Activation verification response:', data);
      
      if (data.success) {
        console.log('✅ Verification successful');
        console.log('📋 Response data:', {
          requirePasswordSetup: data.requirePasswordSetup,
          token: data.token ? 'Present' : 'Missing',
          userRole: data.userRole,
          message: data.message
        });
        
        if (data.requirePasswordSetup) {
          console.log('🔑 Password setup required - showing password form');
          // Password setup required - show password setup form
          setOtpVerified(true);
          setAuthToken(data.token);
          setError('');
          
          console.log('📱 Setting session with:', {
            token: data.token ? 'Present' : 'Missing',
            userRole: data.userRole || 'agent',
            phoneNumber: phoneNumber
          });
          
          // Set temporary session to prevent being signed out during password setup
          // This will be properly set with the correct role after password setup
          sessionManager.setSession(data.token, data.userRole || 'agent', phoneNumber);
          
          console.log('✅ Session set, otpVerified state should be true');
        } else {
          console.log('🚀 Direct activation - redirecting to messages');
          // Direct activation (shouldn't happen for agents, but handle gracefully)
          if (data.token) {
            sessionManager.setSession(data.token, data.userRole || 'agent', phoneNumber);
          }
          navigate('/messages');
        }
      } else {
        console.log('❌ Verification failed:', data.message);
        setError(data.message || 'Invalid verification code');
      }
    } catch (err) {
      console.error('Error verifying code:', err);
      setError('Failed to verify code');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsSubmitting(false);
      return;
    }

    // Validate password strength (basic client-side validation)
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsSubmitting(false);
      return;
    }

    try {
      const requestData = {
        phoneNumber: phoneNumber,
        password: password,
        authToken: authToken,
        isSignUp: false, // Agent already exists, just setting password
        userRole: 'agent'
      };
      
      console.log('🔐 Sending password setup request:', {
        phoneNumber: phoneNumber,
        password: password ? 'Present' : 'Missing',
        authToken: authToken ? 'Present' : 'Missing',
        isSignUp: false,
        userRole: 'agent'
      });
      
      const response = await fetch(`${API_CONFIG.AUTH_BASE_URL}${API_CONFIG.ENDPOINTS.SET_PASSWORD}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();
      console.log('🔐 Password setup response:', { status: response.status, data });

      if (response.ok) {
        console.log('✅ Password set successfully, redirecting to messages');
        // Password set successfully, complete the activation
        sessionManager.setSession(authToken, 'agent', phoneNumber);
        
        // Redirect to messages page
        navigate('/messages');
      } else {
        console.log('❌ Password setup failed:', data);
        setError(data.message || 'Failed to set password');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      console.error('Password setting error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper functions from ChatPage.js
  const formatPrice = (price) => {
    if (!price || price === 0) return 'Contact for Price';
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return 'Contact for Price';
    return `$${numPrice.toLocaleString()}`;
  };

  const getSourceIconAndLink = (listing) => {
    const source = listing.source?.toLowerCase() || 'realtor';
    
    switch (source) {
      case 'facebook':
        return {
          iconUrl: '/facebook-marketplace.png',
          iconAlt: 'Facebook Marketplace',
          linkUrl: listing.external_url || 'https://facebook.com/marketplace',
          linkTitle: 'View on Facebook Marketplace'
        };
      case 'kijiji':
        return {
          iconUrl: '/kijiji-logo.png',
          iconAlt: 'Kijiji',
          linkUrl: listing.external_url || 'https://kijiji.ca',
          linkTitle: 'View on Kijiji'
        };
      case 'realtor':
      case 'ddf':
      default:
        return {
          iconUrl: 'https://www.realtor.ca/images/en-ca/powered_by_realtor.svg',
          iconAlt: 'REALTOR.ca',
          linkUrl: listing.external_url || 'https://realtor.ca',
          linkTitle: 'View on REALTOR.ca'
        };
    }
  };

  // Helper functions to extract listing data (same logic as chat search results)

  const getAgentOffice = (data) => {
    if (data.agent_office) return data.agent_office;
    try {
      if (data.raw_data) {
        const raw = typeof data.raw_data === 'string' ? JSON.parse(data.raw_data) : data.raw_data;
        // Common DDF fields
        if (raw?.ListOfficeName) return raw.ListOfficeName;
        if (raw?.OriginatingSystemName) return raw.OriginatingSystemName;
      }
    } catch (e) {}
    return '';
  };

  const getAgentName = (data) => {
    if (data.agent_first_name || data.agent_last_name) {
      return `${data.agent_first_name || ''} ${data.agent_last_name || ''}`.trim();
    }
    try {
      if (data.raw_data) {
        const raw = typeof data.raw_data === 'string' ? JSON.parse(data.raw_data) : data.raw_data;
        if (raw?.ListAgentFullName) return raw.ListAgentFullName;
      }
    } catch (e) {}
    return '';
  };





  // Convert activation data to listing format for the card (from ChatPage.js)
  const convertActivationToListing = (activation) => {
    if (!activation) return null;

    // Parse photos from media_urls
    let photos = [];
    try {
      if (activation.media_urls) {
        const mediaUrls = typeof activation.media_urls === 'string' 
          ? JSON.parse(activation.media_urls) 
          : activation.media_urls;
        
        if (Array.isArray(mediaUrls)) {
          // DDF format: [{'url': 'photo1.jpg', 'order': 1}, ...]
          if (mediaUrls.length > 0 && typeof mediaUrls[0] === 'object' && mediaUrls[0].url) {
            photos = mediaUrls
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(photo => ({ url: photo.url }));
          } else if (typeof mediaUrls[0] === 'string') {
            // Simple array of URLs
            photos = mediaUrls.map(url => ({ url }));
          }
        } else if (typeof mediaUrls === 'object' && mediaUrls.primary) {
          // Object format with primary/others
          photos = [{ url: mediaUrls.primary }];
          if (mediaUrls.others && Array.isArray(mediaUrls.others)) {
            photos.push(...mediaUrls.others.map(url => ({ url })));
          }
        }
      }
    } catch (e) {
      console.error('Error parsing media_urls:', e);
    }

    // Parse raw_data for additional info
    let rawData = {};
    try {
      if (activation.raw_data) {
        rawData = typeof activation.raw_data === 'string' 
          ? JSON.parse(activation.raw_data) 
          : activation.raw_data;
      }
    } catch (e) {
      console.error('Error parsing raw_data:', e);
    }

    // Build address
    let address = 'Address not available';
    if (rawData.UnparsedAddress) {
      address = rawData.UnparsedAddress;
      if (rawData.PostalCode || activation.postal_code) {
        address += ` ${rawData.PostalCode || activation.postal_code}`;
      }
    } else {
      const parts = [];
      if (activation.neighborhood && activation.neighborhood !== 'Unknown Area') {
        parts.push(activation.neighborhood);
      }
      if (activation.city) parts.push(activation.city);
      if (activation.province) parts.push(activation.province);
      if (parts.length > 0) {
        address = parts.join(', ');
      }
    }

    return {
      id: activation.listing_id,
      photos: photos,
      price: activation.price,
      address: address,
      neighborhood: activation.neighborhood,
      city: activation.city,
      postal_code: activation.postal_code,
      property_type: activation.property_type,
      bedrooms: activation.number_bedrooms,
      bathrooms: rawData.BathroomTotal || rawData.Bathrooms || 1,
      parking_spaces: rawData.ParkingSpaces || rawData.Parking || 1,
      source: activation.source || 'realtor',
      external_url: activation.external_url,
      agent: {
        first_name: activation.agent_first_name,
        last_name: activation.agent_last_name
      },
      agent_details: {
        name: activation.agent_first_name && activation.agent_last_name 
          ? `${activation.agent_first_name} ${activation.agent_last_name}`
          : rawData.ListingAgent?.Name || rawData.Agent?.Name || 'Agent Name',
        office: activation.agent_office || rawData.ListingAgent?.Office || rawData.Agent?.Office || 'Real Estate Office',
        photo: activation.agent_photo || rawData.ListingAgent?.Photo || rawData.Agent?.Photo
      },
      date_listed: activation.date_listed,
      origin_post_id: activation.origin_post_id,
      translated_content: activation.translated_content || rawData.PublicRemarks || 'Spacious property with modern amenities and convenient location.'
    };
  };

  if (loading) {
    return (
      <div className="activation-page">
        {/* Header matching homepage design */}
        <SiteHeader />
        <div className="activation-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading activation details...</div>
            <div className="loading-subtext">Please wait while we verify your activation link</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activation-page">
        {/* Header matching homepage design */}
        <SiteHeader />
        <div className="activation-container">
          <div className="error-message">
            <div className="error-message-content">
              <div className="error-illustration">
                <div className="broken-link-icon">
                  🔗💔
                </div>
              </div>
              <h2>Activation Link Invalid</h2>
              <p>{error}</p>
              <p>This link may have expired or already been used.</p>
              <div style={{ marginTop: '32px' }}>
                <a href="/" className="cta-button">
                  Return to Homepage
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="activation-page">
      {/* Header matching homepage design */}
      <SiteHeader />

      <div className="activation-container">
        {activationData && (
          <div className="activation-content">
            {/* Call to Action - Moved to top */}
            <div className="cta-card">
              <h2>Access Full Applications</h2>
                <p>Sign up to view the complete rental application with credit report and detailed profile.</p>
                
                {!showSignup ? (
                  <button 
                    className="cta-button primary"
                    onClick={handleSignupClick}
                  >
                    Sign Up & View Applications
                  </button>
                ) : (
                  <div className="signup-form">
                    {(() => {
                      console.log('🎨 Rendering signup form with state:', {
                        showVerification,
                        otpVerified,
                        authToken: authToken ? 'Present' : 'Missing'
                      });
                      return null;
                    })()}
                    {!showVerification ? (
                      <form onSubmit={handlePhoneSubmit}>
                        <div className="form-group">
                          <label htmlFor="phone">Phone Number</label>
                          <input
                            type="tel"
                            id="phone"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            placeholder="(555) 123-4567"
                            required
                          />
                        </div>
                        <button 
                          type="submit" 
                          disabled={isSubmitting}
                          className="submit-button"
                        >
                          {isSubmitting ? 'Sending...' : 'Send Code'}
                        </button>
                      </form>
                    ) : !otpVerified ? (
                      <form onSubmit={handleVerificationSubmit}>
                        <div className="form-group">
                          <label htmlFor="code">Verification Code</label>
                          <p className="help-text">Code sent to {phoneNumber}</p>
                          <input
                            type="text"
                            id="code"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            placeholder="123456"
                            maxLength="6"
                            required
                          />
                        </div>
                        <button 
                          type="submit" 
                          disabled={isSubmitting}
                          className="submit-button"
                        >
                          {isSubmitting ? 'Verifying...' : 'Verify & Access'}
                        </button>
                        <button 
                          type="button" 
                          onClick={() => setShowVerification(false)}
                          className="back-button"
                        >
                          ← Back
                        </button>
                      </form>
                    ) : (
                      <>
                        {console.log('🔑 Rendering password setup form!')}
                        <form onSubmit={handlePasswordSubmit}>
                        <div className="form-group">
                          <label htmlFor="password">Create Password</label>
                          <p className="help-text">Choose a secure password for your agent account</p>
                          <div className="password-input-wrapper">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              id="password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              placeholder="Enter password"
                              required
                              minLength="8"
                            />
                            <button
                              type="button"
                              className="password-toggle"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                          </div>
                        </div>
                        <div className="form-group">
                          <label htmlFor="confirmPassword">Confirm Password</label>
                          <div className="password-input-wrapper">
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              id="confirmPassword"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="Confirm password"
                              required
                              minLength="8"
                            />
                            <button
                              type="button"
                              className="password-toggle"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                          </div>
                        </div>
                        <button 
                          type="submit" 
                          disabled={isSubmitting}
                          className="submit-button"
                        >
                          {isSubmitting ? 'Setting Password...' : 'Complete Setup'}
                        </button>
                      </form>
                      </>
                    )}
                  </div>
                )}
                
                {error && <div className="error-message">{error}</div>}
            </div>

            {/* Agent Info Header */}
            <div className="agent-info-header">
              <div className="agent-avatar-large">
                {activationData.agent_photo ? (
                  <img src={activationData.agent_photo} alt="Agent" />
                ) : (
                  <div className="agent-placeholder-large">👔</div>
                )}
              </div>
              <div className="agent-details">
                <h2 className="agent-name-large">
                  {getAgentName(activationData) || 'Real Estate Agent'}
                </h2>
                <p className="agent-office-large">
                  {getAgentOffice(activationData) || 'Real Estate Office'}
                </p>
                <div className="agent-stats">
                  <span className="stat-item">
                    📊 {(agentListings.length + 1)} Active Listings
                  </span>
                  <span className="stat-item">
                    📋 1 New Application
                  </span>
                </div>
              </div>
            </div>

            {/* Listings with embedded applications */}
            <div className="listings-with-applications">
              <div className="section-header">
                <h2>🏢 Agent's Active Listings & Applications</h2>
                <p>All properties managed by {getAgentName(activationData) || 'this agent'} with their applications</p>
              </div>
                
              {/* Current listing (the one being applied to) with its application */}
              <div className="activation-listing-row current-activation-listing-row">
                <div className="listing-main">
                  {(() => {
                      const listing = convertActivationToListing(activationData);
                      if (!listing) return null;
                      
                      return (
                        <div key={listing.id} className="listing-card compact current-listing">
                          <PhotoGallery photos={listing.photos} listingId={listing.id} />
                          <div className="listing-content">
                            <div className="compact-price-header">
                              <div className="compact-price">
                                {formatPrice(listing.price)}
                                {listing.price && parseInt(listing.price) > 0 && <span className="price-period">/month</span>}
                              </div>
                              {(() => {
                                const sourceInfo = getSourceIconAndLink(listing);
                                return (
                                  <a 
                                    href={sourceInfo.linkUrl.startsWith('http') ? sourceInfo.linkUrl : `https://${sourceInfo.linkUrl}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="compact-source-link"
                                    title={sourceInfo.linkTitle}
                                  >
                                    <img 
                                      width="60" 
                                      src={sourceInfo.iconUrl} 
                                      alt={sourceInfo.iconAlt}
                                      onError={(e) => {
                                        if (listing.source?.toLowerCase() === 'facebook') {
                                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0iIzQyNjdCMiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHJ4PSI4IiBmaWxsPSIjNDI2N0IyIi8+CiAgPHRleHQgeD0iMzAiIHk9IjM4IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMzAiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+ZjwvdGV4dD4KPC9zdmc+';
                                          e.target.alt = 'Facebook';
                                        } else if (listing.source?.toLowerCase() === 'kijiji') {
                                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0iIzAwNzFjZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHJ4PSI4IiBmaWxsPSIjMDA3MWNlIi8+CiAgPHRleHQgeD0iMzAiIHk9IjM4IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjQiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SzwvdGV4dD4KPC9zdmc+';
                                          e.target.alt = 'Kijiji';
                                        } else {
                                          e.target.src = 'https://www.realtor.ca/images/en-ca/powered_by_realtor.svg';
                                          e.target.alt = 'REALTOR.ca';
                                        }
                                      }}
                                    />
                                  </a>
                                );
                              })()}
                            </div>
                            <div className="compact-address-section">
                              <h3 className="compact-address">
                                {listing.address || 'Address not available'}
                                {listing.postal_code && ` ${listing.postal_code}`}
                              </h3>
                            </div>
                            <div className="compact-location">
                              📍 {(() => {
                                const parts = [];
                                if (listing.neighborhood && listing.neighborhood !== 'Unknown Area') parts.push(listing.neighborhood);
                                if (listing.city && listing.city !== 'Unknown City') parts.push(listing.city);
                                return parts.length > 0 ? parts.join(', ') : 'Location not specified';
                              })()}
                            </div>
                            <div className="compact-details">
                              <div className="compact-property-info">
                                <span className="compact-property-type">{listing.property_type || 'Property'}</span>
                                <div className="compact-specs">
                                  <span className="spec-item">🛏️ {listing.bedrooms || '0'}</span>
                                  <span className="spec-item">🚿 {listing.bathrooms || '0'}</span>
                                  {listing.parking_spaces && <span className="spec-item">🅿️ {listing.parking_spaces}</span>}
                                </div>
                              </div>
                            </div>
                            <div className="compact-description">
                              {listing.translated_content || 'Spacious property with modern amenities and convenient location.'}
                            </div>
                            <div className="compact-agent-info">
                              <div className="compact-agent-name">
                                {listing.agent_details?.name || 'Agent Name'}
                              </div>
                              <div className="compact-agent-office">
                                {listing.agent_details?.office || 'Real Estate Office'}
                              </div>
                            </div>
                            <div className="compact-listing-meta">
                              {listing.date_listed && (
                                <div className="compact-days-on-market">
                                  {(() => {
                                    const days = Math.floor((new Date() - new Date(listing.date_listed)) / (1000 * 60 * 60 * 24));
                                    return days === 0 ? '0 Days' : `${days} Days`;
                                  })()}
                                </div>
                              )}
                              {listing.origin_post_id && (
                                <div className="compact-listing-id">ID: {listing.origin_post_id}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                </div>
                
                {/* Applications for this listing */}
                <div className="listing-applications">
                  <div className="applications-header">
                    <h4>📋 Applications (1)</h4>
                  </div>
                  <div className="application-card current-application">
                    <div className="application-header">
                      <div className="application-status">Pending Review</div>
                    </div>
                    <div className="application-content">
                      <div className="application-details">
                        <div className="detail-row">
                          <span className="detail-label">Name:</span>
                          <span className="detail-value">
                            {(() => {
                              // Extract name from teaser message
                              const teaserContent = activationData.teaser_message_content || '';
                              const nameMatch = teaserContent.match(/Applicant:\s*([^,]+)/);
                              return nameMatch ? nameMatch[1].trim() : 'N/A';
                            })()}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Job:</span>
                          <span className="detail-value">
                            {activationData.occupation || 'Not specified'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">House Hold:</span>
                          <span className="detail-value">
                            {(() => {
                              const adults = activationData.num_adults || 0;
                              const kids = activationData.num_kids || 0;
                              if (adults === 0 && kids === 0) return 'Not specified';
                              
                              const adultText = adults === 1 ? '1 adult' : `${adults} adults`;
                              const kidText = kids === 0 ? '' : kids === 1 ? '1 kid' : `${kids} kids`;
                              
                              if (kids === 0) return adultText;
                              return `${adultText}, ${kidText}`;
                            })()}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Income:</span>
                          <span className="detail-value blurred">
                            {activationData.monthly_income ? 
                              `$${activationData.monthly_income.toLocaleString()}` : 'Not specified'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Credit Score:</span>
                          <span className="detail-value blurred">
                            {(() => {
                              const score = activationData.credit_score;
                              if (!score) return 'Not specified';
                              // Convert to range format
                              if (score >= 800) return '800+';
                              if (score >= 750) return '750-799';
                              if (score >= 700) return '700-749';
                              if (score >= 650) return '650-699';
                              if (score >= 600) return '600-649';
                              return `${score}+`;
                            })()}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Move-in Date:</span>
                          <span className="detail-value">
                            {activationData.target_move_in_date ? 
                              new Date(activationData.target_move_in_date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long', 
                                day: 'numeric'
                              }) : 'Not specified'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Pet:</span>
                          <span className="detail-value">
                            {activationData.pet === 1 ? 'Yes' : activationData.pet === 0 ? 'No' : 'Not specified'}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Smoke:</span>
                          <span className="detail-value">
                            {activationData.smoking === 1 ? 'Yes' : activationData.smoking === 0 ? 'No' : 'Not specified'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Other agent listings */}
              {agentListings.length > 0 && agentListings.map((listing) => (
                <div key={listing.id} className="activation-listing-row other-activation-listing-row">
                  <div className="listing-main">
                    <div className="listing-card compact other-listing">
                      <div className="listing-image">
                        {listing.image_url ? (
                          <img src={listing.image_url} alt={listing.title} />
                        ) : (
                          <div className="placeholder-image">🏠</div>
                        )}
                      </div>
                      <div className="listing-content">
                        <div className="compact-price-header">
                          <div className="compact-price">
                            ${listing.monthly_equivalent?.toLocaleString()}/month
                          </div>
                        </div>
                        <h3 className="compact-address">{listing.title}</h3>
                        <div className="compact-location">📍 {listing.location}</div>
                        <div className="compact-specs">
                          <span className="spec-item">🛏️ {listing.bedrooms}</span>
                          <span className="spec-item">🏠 {listing.property_type}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Applications for this listing (none for other listings in this case) */}
                  <div className="listing-applications">
                    <div className="applications-header">
                      <h4>📋 Applications (0)</h4>
                    </div>
                    <div className="no-applications">
                      <p>No applications yet</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>





          </div>
        )}
      </div>
    </div>
  );
};

export default ActivationPage;
